import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { usePolling } from "@/lib/usePolling";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DataTable } from "@/components/DataTable";
import { Check, X, Inbox, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import ReceiptButton from "@/components/ReceiptButton";
import RequestEditDialog from "@/components/RequestEditDialog";

const fmtEur = (n) =>
  "€ " + new Intl.NumberFormat("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Rome" }) : "-";
const fmtDateTime = (s) =>
  s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Rome" }) : "-";

export default function AdminRequests() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reject, setReject] = useState(null);
  const [note, setNote] = useState("");
  const [detail, setDetail] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [editReq, setEditReq] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/requests", { params: { status: "in_attesa" } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  const refresh = () =>
    api.get("/requests", { params: { status: "in_attesa" } }).then((r) => setItems(r.data)).catch(() => {});

  useEffect(load, []);
  usePolling(refresh, 15000);

  const approve = async (id) => {
    try {
      await api.post(`/requests/${id}/review`, { action: "approve" });
      toast.success("Richiesta approvata");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doReject = async () => {
    try {
      await api.post(`/requests/${reject.id}/review`, { action: "reject", review_note: note });
      toast.success("Richiesta rifiutata");
      setReject(null);
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/requests/${toDelete.id}`);
      toast.success("Richiesta eliminata");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const columns = [
    { key: "employee_name", header: "Dipendente", sortable: true, className: "font-medium text-slate-900", cell: (r) => (
        <span>{r.employee_name}{r.source === "flux" ? <span className="ml-2 inline-block rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[10px] font-medium text-violet-700" data-testid={`flux-badge-${r.id}`}>Flux</span> : null}{r.created_by_name ? <span className="ml-2 inline-block rounded-full border border-blue-200 bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-[#002FA7]" data-testid={`on-behalf-badge-${r.id}`}>Per conto di · {r.created_by_name}</span> : null}{r.edit_requested ? <span className="ml-2 inline-block rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700" data-testid={`edit-req-badge-${r.id}`}>Modifica richiesta</span> : null}</span>
      ) },
    { key: "fuel_card", header: "Carta", sortable: true, className: "text-slate-600", cell: (r) => r.fuel_card || "-" },
    { key: "vehicle_name", header: "Mezzo", sortable: true, className: "text-slate-600", cell: (r) => (
        <>
          {r.vehicle_name}
          {r.vehicle_plate && <span className="block text-xs text-slate-400">{r.vehicle_plate}</span>}
        </>
      ) },
    { key: "usage", header: "Utilizzo", sortable: true, sortValue: (r) => r.usage_value, className: "text-slate-600", cell: (r) => `${r.usage_value} ${r.usage_type === "km" ? "km" : "ore"}` },
    { key: "fuel_type", header: "Carburante", sortable: true, className: "text-slate-600", cell: (r) => r.fuel_type },
    { key: "amount", header: "Importo", align: "right", sortable: true, className: "text-right", cell: (r) => (
        <div className="text-right">
          <div className="font-heading font-bold text-slate-900">{fmtEur(r.amount)}</div>
          {r.actual_amount != null && (
            <div className="text-xs font-normal text-slate-500">eff. {fmtEur(r.actual_amount)}</div>
          )}
        </div>
      ) },
    { key: "created_at", header: "Data", sortable: true, sortValue: (r) => new Date(r.created_at).getTime(), className: "text-slate-500", cell: (r) => fmtDate(r.created_at) },
    { key: "status", header: "Stato", sortable: true, cell: (r) => <StatusBadge status={r.status} /> },
    { key: "receipt", header: "Scontrino", cell: (r) => (r.receipt_path ? <span onClick={(e) => e.stopPropagation()}><ReceiptButton path={r.receipt_path} /></span> : <span className="text-slate-300">—</span>) },
    { key: "actions", header: "Azioni", align: "right", cell: (r) => (
        <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
          {r.status === "in_attesa" ? (
            <>
              <Button size="sm" onClick={() => approve(r.id)} data-testid={`approve-btn-${r.id}`} className="rounded-md bg-emerald-600 text-white hover:bg-emerald-700">
                <Check className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="outline" onClick={() => setReject(r)} data-testid={`reject-btn-${r.id}`} className="rounded-md border-rose-200 text-rose-600 hover:bg-rose-50">
                <X className="h-4 w-4" />
              </Button>
            </>
          ) : (
            <span className="self-center text-xs text-slate-400">{r.reviewed_by}</span>
          )}
          <Button size="sm" variant="outline" onClick={() => setEditReq(r)} data-testid={`edit-request-btn-${r.id}`} className="rounded-md border-slate-200"><Pencil className="h-4 w-4" /></Button>
          {isAdmin && (
            <Button size="sm" variant="outline" onClick={() => setToDelete(r)} data-testid={`delete-btn-${r.id}`} className="rounded-md border-slate-200 text-slate-500 hover:bg-rose-50 hover:text-rose-600">
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      ) },
  ];

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">
            Richieste di rifornimento
          </h1>
          <p className="mt-1 text-sm text-slate-500">Solo le richieste in attesa di approvazione. Le altre sono nello Storico richieste.</p>
        </div>
      </div>

      <Card className="rounded-lg border-slate-200 shadow-sm">
        {loading ? (
          <p className="p-6 text-slate-400">Caricamento...</p>
        ) : (
          <div className="p-4">
            <DataTable
              data={items}
              testid="admin-requests-table"
              rowTestId={(r) => `admin-request-row-${r.id}`}
              onRowClick={(r) => setDetail(r)}
              searchKeys={["employee_name", "fuel_card", "vehicle_name", "vehicle_plate", "fuel_type"]}
              searchPlaceholder="Cerca richiesta..."
              initialSort={{ key: "created_at", dir: "desc" }}
              emptyIcon={Inbox}
              emptyText="Nessuna richiesta"
              columns={columns}
            />
          </div>
        )}
      </Card>

      <Dialog open={!!reject} onOpenChange={(o) => !o && setReject(null)}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Rifiuta richiesta</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">
              Rifiuti la richiesta di <b>{reject?.employee_name}</b> da {fmtEur(reject?.amount)}?
            </p>
            <div>
              <Label htmlFor="rej-note">Motivo (facoltativo)</Label>
              <Input
                id="rej-note"
                data-testid="reject-note-input"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1.5"
                placeholder="Motivo del rifiuto"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReject(null)}>Annulla</Button>
            <Button onClick={doReject} data-testid="confirm-reject-btn" className="bg-rose-600 text-white hover:bg-rose-700">
              Rifiuta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="admin-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Storico richiesta</DialogTitle>
            <DialogDescription>
              {detail?.employee_name} · {detail?.vehicle_name} · {fmtEur(detail?.amount)}
            </DialogDescription>
          </DialogHeader>
          <ol className="relative ml-1 space-y-5 border-l border-slate-200 pl-6">
            <li className="relative">
              <span className="absolute -left-[30px] mt-1 h-3.5 w-3.5 rounded-full bg-[#002FA7]" />
              <p className="font-medium text-slate-900">Richiesta creata</p>
              <p className="text-sm text-slate-500" data-testid="admin-detail-created">{fmtDateTime(detail?.created_at)}</p>
            </li>
            <li className="relative">
              <span className={`absolute -left-[30px] mt-1 h-3.5 w-3.5 rounded-full ${detail?.status === "approvata" ? "bg-emerald-500" : detail?.status === "rifiutata" ? "bg-rose-500" : "bg-slate-300"}`} />
              <p className="font-medium text-slate-900">
                {detail?.status === "rifiutata" ? "Richiesta rifiutata" : detail?.status === "approvata" ? "Richiesta approvata" : "In attesa di approvazione"}
              </p>
              {detail?.reviewed_at ? (
                <p className="text-sm text-slate-500" data-testid="admin-detail-reviewed">
                  {fmtDateTime(detail.reviewed_at)}{detail.reviewed_by ? ` · da ${detail.reviewed_by}` : ""}
                </p>
              ) : (
                <p className="text-sm text-slate-400">Non ancora gestita</p>
              )}
            </li>
            <li className="relative">
              <span className={`absolute -left-[30px] mt-1 h-3.5 w-3.5 rounded-full ${(detail?.receipt_uploaded_at || detail?.receipt_path) ? "bg-emerald-500" : "bg-slate-300"}`} />
              <p className="font-medium text-slate-900">Scontrino caricato</p>
              {detail?.receipt_uploaded_at ? (
                <p className="text-sm text-slate-500" data-testid="admin-detail-receipt">
                  {fmtDateTime(detail.receipt_uploaded_at)}{detail.actual_amount != null ? ` · Importo effettivo ${fmtEur(detail.actual_amount)}` : ""}
                </p>
              ) : detail?.receipt_path ? (
                <p className="text-sm text-slate-500" data-testid="admin-detail-receipt">
                  Caricato{detail.actual_amount != null ? ` · Importo effettivo ${fmtEur(detail.actual_amount)}` : ""}
                </p>
              ) : (
                <p className="text-sm text-slate-400">Non caricato</p>
              )}
            </li>
          </ol>
        </DialogContent>
      </Dialog>
      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent data-testid="delete-request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Elimina richiesta</DialogTitle>
            <DialogDescription>
              Eliminare definitivamente la richiesta di <b>{toDelete?.employee_name}</b> da {fmtEur(toDelete?.amount)}? L'operazione non è reversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>Annulla</Button>
            <Button onClick={doDelete} data-testid="confirm-delete-btn" className="bg-rose-600 text-white hover:bg-rose-700">
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <RequestEditDialog request={editReq} open={!!editReq} onClose={() => setEditReq(null)} onSaved={load} />
    </div>
  );
}
